import ApiService from './api.service'
import type { UserNotificationDTO } from '@/types'

/**
 * Service for managing user notifications API endpoints
 */
class UserNotificationService extends ApiService {
  private readonly baseUrl = '/api/notifications'

  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Notification Error'
      }
    })
  }

  /**
   * Get all notifications for the current user
   * @returns Promise that resolves with all user notifications
   */
  async getNotifications(): Promise<UserNotificationDTO[]> {
    const response = await this.get<UserNotificationDTO[]>(this.baseUrl)
    return response || []
  }

  /**
   * Get unread notifications for the current user
   * @returns Promise that resolves with unread notifications
   */
  async getUnreadNotifications(): Promise<UserNotificationDTO[]> {
    const response = await this.get<UserNotificationDTO[]>(`${this.baseUrl}/unread`)
    return response || []
  }

  /**
   * Get count of unread notifications for the current user
   * @returns Promise that resolves with the count of unread notifications
   */
  async getUnreadCount(): Promise<number> {
    const response = await this.get<{ count: number }>(`${this.baseUrl}/unread/count`)
    return response?.count || 0
  }

  /**
   * Mark a notification as read
   * @param notificationId - The notification ID
   * @returns Promise that resolves when the notification is marked as read
   */
  async markAsRead(notificationId: number): Promise<void> {
    await this.put<void>(`${this.baseUrl}/${notificationId}/read`, {})
  }

  /**
   * Mark all notifications as read for the current user
   * @returns Promise that resolves when all notifications are marked as read
   */
  async markAllAsRead(): Promise<void> {
    await this.put<void>(`${this.baseUrl}/read-all`, {})
  }

  /**
   * Delete a notification
   * @param notificationId - The notification ID
   * @returns Promise that resolves when the notification is deleted
   */
  async deleteNotification(notificationId: number): Promise<void> {
    await this.delete(`${this.baseUrl}/${notificationId}`)
  }
}

export const userNotificationService = new UserNotificationService()
export default userNotificationService
